import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, Share2, Trash2, Wand2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { formatDateTime, formatNumber } from "@/lib/format";

const QUALITY = { budget: "Budget", premium: "Premium" };
const SHARED = { pending: "In review", published: "Shared", rejected: "Not approved" };

function useElapsed(since, active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  const s = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function Badges({ g, preset }) {
  const tags = [g.operation === "edit" ? "Edited" : null, QUALITY[g.quality], g.kind === "video" ? "Video" : null, preset ? SHARED[preset.status] : null].filter(Boolean);
  return tags.map((t) => (
    <span key={t} className="rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-100 backdrop-blur">{t}</span>
  ));
}

function CancelButton({ g }) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => api.post(`/media/generations/${g.id}/cancel`),
    onSuccess: () => { toast.success("Canceling…"); qc.invalidateQueries({ queryKey: ["media-generations"] }); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <button type="button" onClick={() => cancel.mutate()} disabled={cancel.isPending} data-testid="generation-cancel"
      className="pointer-events-auto mt-1 inline-flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1 text-[11px] text-zinc-300 hover:border-white/30 hover:text-white">
      <XCircle className="h-3.5 w-3.5" /> Cancel
    </button>
  );
}

function Running({ g }) {
  const elapsed = useElapsed(g.created_at, true);
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[linear-gradient(110deg,#121216_30%,#202027_50%,#121216_70%)] bg-[length:200%_100%] animate-shimmer" data-testid="generation-running">
      <span className="text-xs font-medium text-zinc-300">{g.operation === "edit" ? "Editing…" : "Creating…"}</span>
      <span className="font-mono text-[11px] tabular-nums text-zinc-500" data-testid="generation-elapsed">{elapsed} · {g.kind === "video" ? "videos take a few minutes" : "usually under a minute"}</span>
      {g.provider === "higgsfield" && g.submitted && <CancelButton g={g} />}
    </div>
  );
}

function Preview({ g, onOpen }) {
  if (g.status === "running") return <Running g={g} />;
  if (g.status === "failed" || g.status === "canceled") return <p className="px-4 text-center text-xs text-zinc-400" data-testid="generation-failed">{g.error_message}</p>;
  if (g.kind === "video") return <video src={g.url} controls playsInline className="h-full w-full object-contain" data-testid="generation-video" />;
  return (
    <button type="button" onClick={onOpen} className="h-full w-full" aria-label="View full size" data-testid="generation-open">
      <img src={g.thumb_url || g.url} alt={g.prompt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" data-testid="generation-image" />
    </button>
  );
}

const iconBtn = "flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-100";

export function GenerationCard({ g, onEdit, onPublish, preset }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.del(`/media/generations/${g.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media-generations"] }),
    onError: (e) => toast.error(e.message),
  });
  const done = g.kind === "image" && g.status === "complete";
  return (
    <div className="group overflow-hidden rounded-2xl border border-white/[0.08] bg-ink-850 transition-colors hover:border-white/15" data-testid={`generation-card-${g.id}`}>
      <div className="relative flex aspect-square items-center justify-center overflow-hidden bg-ink-900">
        <Preview g={g} onOpen={() => setOpen(true)} />
        <div className="pointer-events-none absolute left-2 top-2 flex flex-wrap gap-1"><Badges g={g} preset={preset} /></div>
      </div>
      <div className="p-3">
        <p className="line-clamp-2 min-h-[2rem] text-xs text-zinc-300">{g.prompt}</p>
        {g.model_label && <p className="mt-1 truncate text-[10px] font-medium uppercase tracking-wider text-zinc-500" data-testid="generation-model">{g.model_label}</p>}
        <p className="mt-1 truncate text-[11px] text-zinc-600">{formatDateTime(g.created_at)} · {g.credits_charged ? `${formatNumber(g.credits_charged)} credits` : "No charge"}</p>
        <div className="-mx-1.5 mt-1.5 flex items-center" data-testid="generation-actions">
            {done && onEdit && <button type="button" onClick={() => onEdit(g)} className={iconBtn} data-testid="generation-edit" aria-label="Edit this image"><Wand2 className="h-4 w-4" /></button>}
            {done && onPublish && !preset && <button type="button" onClick={() => onPublish(g)} className={iconBtn} data-testid="generation-publish" aria-label="Share as preset"><Share2 className="h-4 w-4" /></button>}
            {g.url && <a href={g.url} target="_blank" rel="noreferrer" download className={iconBtn} data-testid="generation-download" aria-label="Download"><Download className="h-4 w-4" /></a>}
            {g.status !== "running" && (
              <button type="button" onClick={() => remove.mutate()} className={`${iconBtn} hover:text-red-300`} data-testid="generation-delete" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
            )}
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[min(94vw,1100px)] border-white/10 bg-ink-950 p-3" data-testid="generation-lightbox">
          <DialogTitle className="sr-only">{g.prompt}</DialogTitle>
          <img src={g.url} alt={g.prompt} className="max-h-[82vh] w-full rounded-lg object-contain" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
